import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    ProductState,
    useGetColumnProductsQuery,
    useGetColumnProductsLazyQuery,
    useGetColumnProductQuery,
} from 'data/__generated__';
import { Option, TotemSelect } from 'components/TotemSelect';

const PAGE_SIZE = 30;
const PRODUCT_FILTERS = {
    states: [ProductState.Regular, ProductState.Created],
};

const formatLabel = (fullname: string, stock: number): string => `${fullname} — stock: ${stock}`;

type ProductWithStocksSelectorProps = {
    productOption: string | null;
    handleProductSelect: (productId: string, productLabel: string) => void;
    columnId: string;
    label?: string;
    placeholder?: string;
    dataTest?: string;
};

export const ProductWithStocksSelector = ({
    productOption,
    handleProductSelect,
    columnId,
    label = 'Ajouter un produit',
    placeholder = 'Sélectionner un produit (recherche par nom ou par _id)',
    dataTest = 'product-selector',
}: ProductWithStocksSelectorProps) => {
    const [selectedOptionCache, setSelectedOptionCache] = useState<Option<string> | null>(null);

    // Every option we've shown (defaults + search results), keyed by product id. A click only hands
    // us the value, so we resolve it here — including products surfaced by a search that were never
    // part of defaultOptions (otherwise selecting them silently did nothing).
    const knownOptionsRef = useRef<Map<string, Option<string>>>(new Map());
    const rememberOptions = useCallback((options: Option<string>[]): Option<string>[] => {
        options.forEach((option) => knownOptionsRef.current.set(option.value, option));
        return options;
    }, []);

    const { data: currentColumnProductData } = useGetColumnProductQuery({
        variables: { columnId, productId: productOption ?? '' },
        skip: !productOption,
    });

    // Default options (top N), loaded once per columnId. Never refetched — keeps the initial
    // list stable so clearing the search input restores it.
    const { data: defaultProductsData } = useGetColumnProductsQuery({
        variables: {
            columnId,
            offset: 0,
            limit: PAGE_SIZE,
            filters: { searchValue: '', ...PRODUCT_FILTERS },
        },
        fetchPolicy: 'cache-first',
    });

    // Separate lazy query for search; doesn't share its data with the default list above.
    const [searchColumnProducts] = useGetColumnProductsLazyQuery({ fetchPolicy: 'no-cache' });

    const defaultSearchOptions = useMemo<Option<string>[]>(
        () =>
            (defaultProductsData?.columnProducts.columnProducts ?? []).map(({ product, stock }) => ({
                label: formatLabel(product.fullname, stock),
                value: product._id,
            })),
        [defaultProductsData],
    );

    const currentOption = useMemo<Option<string> | null>(() => {
        const cp = currentColumnProductData?.columnProduct;
        if (cp) {
            return {
                label: formatLabel(cp.product.fullname, cp.stock),
                value: cp.product._id,
            };
        }
        return selectedOptionCache;
    }, [currentColumnProductData, selectedOptionCache]);

    const defaultOptions = useMemo<Option<string>[]>(() => {
        if (currentOption && !defaultSearchOptions.find((o) => o.value === currentOption.value)) {
            return [currentOption, ...defaultSearchOptions];
        }
        return defaultSearchOptions;
    }, [defaultSearchOptions, currentOption]);

    useEffect(() => {
        rememberOptions(defaultOptions);
    }, [defaultOptions, rememberOptions]);

    const fetchOptions = useCallback(
        async (query: string): Promise<Option<string>[]> => {
            const result = await searchColumnProducts({
                variables: {
                    columnId,
                    offset: 0,
                    limit: PAGE_SIZE,
                    filters: { searchValue: query, ...PRODUCT_FILTERS },
                },
            });
            const options = (result.data?.columnProducts.columnProducts ?? []).map(({ product, stock }) => ({
                label: formatLabel(product.fullname, stock),
                value: product._id,
            }));
            return rememberOptions(options);
        },
        [columnId, searchColumnProducts, rememberOptions],
    );

    const handleChange = (value: string | null) => {
        if (!value) {
            setSelectedOptionCache(null);
            return;
        }
        const option = knownOptionsRef.current.get(value) ?? null;
        if (option) {
            setSelectedOptionCache(option);
            handleProductSelect(option.value, option.label);
        }
    };

    return (
        <TotemSelect<string>
            label={label}
            placeholder={placeholder}
            value={productOption}
            onChange={handleChange}
            fetchOptions={fetchOptions}
            defaultOptions={defaultOptions}
            isClearable
            dataTest={dataTest}
        />
    );
};
